import {format} from "date-fns";
import {pl} from "date-fns/locale";

import {upperFirst} from "@pg-mono/nodash";
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {BRAND_NAME} from "../../../../app/constants/company";
import {Country} from "../../../../region/types/Country";
import type {ISelectedRegion} from "../../actions/offer_list_selected_data_actions";
import {MONTH_POLISH_NAMES} from "../../types/month_polish_names";
import {ILocationGenericSection, ParagraphType} from "./constants";

const getMonthYearPhrase = (): {monthLocative: string; year: number; monthYearDisplay: string} => {
    const now = new Date();
    return {
        monthLocative: MONTH_POLISH_NAMES[now.getMonth()],
        year: now.getFullYear(),
        monthYearDisplay: format(now, "LLLL yyyy", {locale: pl})
    };
};

export type MultiRoomFlatFaqRoomCount = 2 | 3 | 4;

export const getMultiRoomFlatFaqLocationSections = (roomCount: MultiRoomFlatFaqRoomCount, region: ISelectedRegion): ILocationGenericSection[] => {
    const stats = region.stats;

    if (!stats || region.country === Country.SPAIN) {
        return [];
    }

    const flatRoomsByCount = stats.properties_flats_rooms_ranges as Record<string, number> | undefined;
    const propertiesForRooms = flatRoomsByCount?.[String(roomCount)] ?? 0;
    if (!propertiesForRooms) {
        return [];
    }

    const where = region.name_declension_where;
    const regionName = region.name;
    const flatsTotal = stats.properties_flats_count_for_sale || 0;
    const sharePercent = flatsTotal > 0 ? Math.min(100, Math.round((100 * propertiesForRooms) / flatsTotal)) : Math.round(propertiesForRooms > 0 ? 100 : 0);

    const offersFlatsCount = stats.offers_count_by_flat_rooms?.[String(roomCount)] || 0;

    const {monthLocative, year, monthYearDisplay} = getMonthYearPhrase();

    const rcKey = String(roomCount);
    const priceByM2 = stats.properties_flats_ranges_price_m2_by_rooms as Record<string, {avg: number} | undefined> | undefined;
    const priceByFlat = stats.properties_flats_ranges_price_by_rooms as Record<string, {min: number} | undefined> | undefined;
    const areaByRooms = stats.properties_flats_ranges_area_by_rooms as Record<string, {min: number; max: number} | undefined> | undefined;
    const priceM2 = priceByM2?.[rcKey]?.avg ?? 0;
    const priceMin = priceByFlat?.[rcKey]?.min ?? 0;
    const areaMin = areaByRooms?.[rcKey]?.min ?? 0;
    const areaMax = areaByRooms?.[rcKey]?.max ?? 0;

    const readyByRoom = stats.properties_flats_count_ready_by_rooms?.[String(roomCount)];
    const readyNow = readyByRoom?.ready ?? 0;
    const ready3m = readyByRoom?.ready_in_3m ?? 0;
    const ready6m = readyByRoom?.ready_in_6m ?? 0;
    const ready12m = readyByRoom?.ready_in_12m ?? 0;

    const readyTimelineItems = (
        [
            [readyNow, "gotowe do odbioru"] as const,
            [ready3m, "odbiór w ciągu 3 miesięcy"] as const,
            [ready6m, "odbiór w ciągu 6 miesięcy"] as const,
            [ready12m, "odbiór w ciągu roku"] as const
        ] as const
    )
        .filter(([count]) => count > 0)
        .map(([count, label], index, rows) => {
            const ending = index === rows.length - 1 ? "." : ",";
            return `${label}: ${numberFormat(count)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(count)}${ending}`;
        });

    const areaBuckets = stats.properties_flats_count_ready_by_rooms_and_area?.[String(roomCount)];
    const up40 = areaBuckets?.up40 ?? 0;
    const up50 = areaBuckets?.up50 ?? 0;
    const up60 = areaBuckets?.up60 ?? 0;

    const twoRoomAreaAvailabilityListItems =
        roomCount === 2
            ? (
                  [
                      [up40, 40],
                      [up50, 50],
                      [up60, 60]
                  ] as const
              )
                  .filter(([count]) => count > 0)
                  .map(([count, limit], index, rows) => {
                      const ending = index === rows.length - 1 ? "." : ",";
                      return `do ${limit} mkw.: ${numberFormat(count)} ${pluralize(["oferta", "oferty", "ofert"])(count)}${ending}`;
                  })
            : [];

    const promotionsCount = stats.promotions_count_total || 0;

    const roomPhraseNominative = `${roomCount} ${pluralize(["pokój", "pokoje", "pokoi"])(roomCount)}`;
    const roomAdjBeforeNoun = `${roomCount}-pokoj${pluralize(["owy", "owe", "owe"])(roomCount)}`;
    const roomAdjGenitivePlural = `${roomCount}-pokoj${pluralize(["owego", "owych", "owych"])(roomCount)}`;
    const roomAdjGenitiveSingular = `${roomCount}-pokoj${pluralize(["owego", "owego", "owego"])(roomCount)}`;

    const introHeadline = `FAQ Mieszkania ${roomAdjBeforeNoun} ${regionName} – dane ${BRAND_NAME}`;

    const countAnswerIntro = `Aktualnie na ${BRAND_NAME} ${where} ${pluralize([
        `dostępne jest ${numberFormat(propertiesForRooms)} nowe mieszkanie ${roomAdjBeforeNoun}`,
        `dostępne są ${numberFormat(propertiesForRooms)} nowe mieszkania ${roomAdjBeforeNoun}`,
        `dostępnych jest ${numberFormat(propertiesForRooms)} nowych mieszkań ${roomAdjGenitivePlural}`
    ])(
        propertiesForRooms
    )}.${offersFlatsCount > 1 ? ` Są one rozmieszczone w ${pluralize([`${numberFormat(offersFlatsCount)} inwestycji deweloperskiej`, `${numberFormat(offersFlatsCount)} inwestycjach deweloperskich`, `${numberFormat(offersFlatsCount)} inwestycjach deweloperskich`])(offersFlatsCount)}.` : ""} ${upperFirst(roomPhraseNominative)} stanowią ${numberFormat(sharePercent)} proc. całej oferty nowych mieszkań ${where}.`;

    const sections: ILocationGenericSection[] = [
        {
            headline: introHeadline,
            paragraphs: []
        },
        {
            headline: `Ile mieszkań ${roomAdjGenitivePlural} jest dostępnych w sprzedaży ${where}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: countAnswerIntro
                },
                ...(roomCount === 4
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `Oferta ${roomCount} pokoi jest bardziej ograniczona niż mniejszych mieszkań – to naturalny efekt rynku. Warto śledzić nowe etapy inwestycji lub uruchomić powiadomienie mailowe.`
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : [])
            ]
        },
        {
            headline:
                roomCount === 2
                    ? `Ile kosztuje mieszkanie ${roomAdjBeforeNoun} ${where} w ${year} roku?`
                    : `Ile kosztuje nowe mieszkanie ${roomAdjBeforeNoun} ${where} w ${year} roku?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: (() => {
                        const parts = [
                            priceM2 > 0
                                ? `Średnia cena ${roomCount === 2 ? "metra kwadratowego mieszkania" : "za metr kwadratowy mieszkania"} ${roomAdjGenitiveSingular} wynosi obecnie ${priceFormat(priceM2)}. `
                                : "",
                            roomCount === 4
                                ? priceMin > 0 &&
                                  `Najtańsze dostępne mieszkanie ${roomAdjBeforeNoun} ${where} kosztuje ${priceFormat(priceMin)} (${monthYearDisplay}).`
                                : priceMin > 0 &&
                                  `Najtańsze nowe mieszkanie ${roomCount} pokojowe ${where} kosztuje w ${monthLocative} ${year} - ${priceFormat(priceMin)}.`
                        ].filter(Boolean) as string[];
                        const joined = parts.join("");
                        return joined || `Szczegółowe ceny mieszkań ${roomAdjGenitivePlural} ${where} zależą od inwestycji — sprawdź listę ofert i filtr ceny.`;
                    })()
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Ceny różnią się istotnie w zależności od lokalizacji, piętra i standardu. W menu rozwijanym "Sortuj" na liście ofert wybierz opcję "Najtańsze", aby uporządkować oferty ${roomCount} pokoi według najniższej ceny.`
                }
            ]
        },
        {
            headline: `Jakie metraże mają mieszkania ${roomAdjBeforeNoun} ${where}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text:
                        areaMin === areaMax
                            ? `Mieszkania ${roomAdjBeforeNoun} mają powierzchnię ${areaMin} mkw.`
                            : `Mieszkania ${roomAdjBeforeNoun} mają powierzchnię od ${areaMin} do ${areaMax} mkw.`
                },
                ...(roomCount === 2
                    ? ([
                          ...(twoRoomAreaAvailabilityListItems.length > 0
                              ? ([
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Dostępność wg metrażu:"
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: twoRoomAreaAvailabilityListItems
                                    }
                                ] as ILocationGenericSection["paragraphs"])
                              : [])
                      ] as ILocationGenericSection["paragraphs"])
                    : roomCount === 3
                      ? ([
                            {
                                type: ParagraphType.TEXT,
                                text: "Standardowy układ to salon z aneksem kuchennym oraz dwie oddzielne sypialnie, co zapewnia prywatność każdemu domownikowi."
                            }
                        ] as ILocationGenericSection["paragraphs"])
                      : ([
                            {
                                type: ParagraphType.TEXT,
                                text: "Standardowy układ obejmuje salon z aneksem kuchennym, 3 oddzielne sypialnie (lub 2 sypialnie + gabinet) oraz najczęściej dwie łazienki lub łazienkę z osobnym WC."
                            }
                        ] as ILocationGenericSection["paragraphs"]))
            ]
        },
        ...(roomCount === 3 || roomCount === 4
            ? ([
                  {
                      headline: `Dla kogo jest mieszkanie ${roomAdjBeforeNoun}?`,
                      paragraphs:
                          roomCount === 3
                              ? [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Mieszkanie ${roomAdjBeforeNoun} ${where} to doskonały wybór dla:`
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: [
                                            "rodzin z jednym dzieckiem – każdy domownik ma własną sypialnię,",
                                            "par pracujących zdalnie – jedna sypialnia jako gabinet,",
                                            "osób szukających lokum z przestrzenią do aranżacji."
                                        ]
                                    },
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Sprawdź filtry na liście – możesz wybrać m.in. mieszkania z ogródkiem na parterze."
                                    }
                                ]
                              : [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Mieszkanie ${roomAdjBeforeNoun} ${where} to idealne rozwiązanie dla:`
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: [
                                            "rodzin z dwójką lub trójką dzieci – każde dziecko ma własny pokój,",
                                            "osób pracujących zdalnie, dla których ważny jest komfortowy gabinet,",
                                            "kupujących, dla których priorytetem jest większa przestrzeń."
                                        ]
                                    },
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Sprawdź opcje z garażem podziemnym i komórką lokatorską."
                                    }
                                ]
                  }
              ] as ILocationGenericSection[])
            : []),
        {
            headline: `Kiedy będzie można odebrać klucze do nowego mieszkania ${roomAdjGenitiveSingular}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Dostępność mieszkań ${roomAdjGenitivePlural} w ${monthLocative} ${year} ${where} wygląda następująco:`
                },
                ...(readyTimelineItems.length > 0
                    ? ([
                          {
                              type: ParagraphType.LIST,
                              text: readyTimelineItems
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),
                {
                    type: ParagraphType.TEXT,
                    text: `Filtruj oferty korzystając z opcji „termin oddania”.`
                }
            ]
        },
        ...(roomCount === 2
            ? ([
                  {
                      headline: `Czy deweloperzy oferują promocje na mieszkania ${roomAdjBeforeNoun} ${where}?`,
                      paragraphs:
                          promotionsCount > 0
                              ? [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Aktualnie deweloperzy przygotowali ${numberFormat(promotionsCount)} ${pluralize([
                                            "ofertę promocyjną",
                                            "oferty promocyjne",
                                            "ofert promocyjnych"
                                        ])(
                                            promotionsCount
                                        )} na zakup mieszkania ${roomAdjGenitiveSingular} ${where}. Warto zapytać dewelopera o szczegóły – promocje często obejmują miejsce postojowe lub obniżoną cenę przy szybkiej decyzji.`
                                    }
                                ]
                              : [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Aktualnie w inwestycjach ${where} nie obowiązują promocje na mieszkania ${roomAdjBeforeNoun}. Zapisz się na newsletter, żeby otrzymać powiadomienie, gdy się to zmieni.`
                                    }
                                ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];

    return sections;
};
import {format} from "date-fns";
import {pl} from "date-fns/locale";

import {upperFirst} from "@pg-mono/nodash";
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {BRAND_NAME} from "../../../../app/constants/company";
import {Country} from "../../../../region/types/Country";
import type {ISelectedRegion} from "../../actions/offer_list_selected_data_actions";
import {MONTH_POLISH_NAMES} from "../../types/month_polish_names";
import {ILocationGenericSection, ParagraphType} from "./constants";

const getMonthYearPhrase = (): {monthLocative: string; year: number; monthYearDisplay: string} => {
    const now = new Date();
    return {
        monthLocative: MONTH_POLISH_NAMES[now.getMonth()],
        year: now.getFullYear(),
        monthYearDisplay: format(now, "LLLL yyyy", {locale: pl})
    };
};

export type MultiRoomFlatFaqRoomCount = 2 | 3 | 4;

export const getMultiRoomFlatFaqLocationSections = (roomCount: MultiRoomFlatFaqRoomCount, region: ISelectedRegion): ILocationGenericSection[] => {
    const stats = region.stats;

    if (!stats || region.country === Country.SPAIN) {
        return [];
    }

    const flatRoomsByCount = stats.properties_flats_rooms_ranges as Record<string, number> | undefined;
    const propertiesForRooms = flatRoomsByCount?.[String(roomCount)] ?? 0;
    if (!propertiesForRooms) {
        return [];
    }

    const where = region.name_declension_where;
    const regionName = region.name;
    const flatsTotal = stats.properties_flats_count_for_sale || 0;
    const sharePercent = flatsTotal > 0 ? Math.min(100, Math.round((100 * propertiesForRooms) / flatsTotal)) : Math.round(propertiesForRooms > 0 ? 100 : 0);

    const offersFlatsCount = stats.offers_count_by_flat_rooms?.[String(roomCount)] || 0;

    const {monthLocative, year, monthYearDisplay} = getMonthYearPhrase();

    const rcKey = String(roomCount);
    const priceByM2 = stats.properties_flats_ranges_price_m2_by_rooms as Record<string, {avg: number} | undefined> | undefined;
    const priceByFlat = stats.properties_flats_ranges_price_by_rooms as Record<string, {min: number} | undefined> | undefined;
    const areaByRooms = stats.properties_flats_ranges_area_by_rooms as Record<string, {min: number; max: number} | undefined> | undefined;
    const priceM2 = priceByM2?.[rcKey]?.avg ?? 0;
    const priceMin = priceByFlat?.[rcKey]?.min ?? 0;
    const areaMin = areaByRooms?.[rcKey]?.min ?? 0;
    const areaMax = areaByRooms?.[rcKey]?.max ?? 0;

    const readyByRoom = stats.properties_flats_count_ready_by_rooms?.[String(roomCount)];
    const readyNow = readyByRoom?.ready ?? 0;
    const ready3m = readyByRoom?.ready_in_3m ?? 0;
    const ready6m = readyByRoom?.ready_in_6m ?? 0;
    const ready12m = readyByRoom?.ready_in_12m ?? 0;

    const readyTimelineItems = (
        [
            [readyNow, "gotowe do odbioru"] as const,
            [ready3m, "odbiór w ciągu 3 miesięcy"] as const,
            [ready6m, "odbiór w ciągu 6 miesięcy"] as const,
            [ready12m, "odbiór w ciągu roku"] as const
        ] as const
    )
        .filter(([count]) => count > 0)
        .map(([count, label], index, rows) => {
            const ending = index === rows.length - 1 ? "." : ",";
            return `${label}: ${numberFormat(count)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(count)}${ending}`;
        });

    const areaBuckets = stats.properties_flats_count_ready_by_rooms_and_area?.[String(roomCount)];
    const up40 = areaBuckets?.up40 ?? 0;
    const up50 = areaBuckets?.up50 ?? 0;
    const up60 = areaBuckets?.up60 ?? 0;

    const twoRoomAreaAvailabilityListItems =
        roomCount === 2
            ? (
                  [
                      [up40, 40],
                      [up50, 50],
                      [up60, 60]
                  ] as const
              )
                  .filter(([count]) => count > 0)
                  .map(([count, limit], index, rows) => {
                      const ending = index === rows.length - 1 ? "." : ",";
                      return `do ${limit} mkw.: ${numberFormat(count)} ${pluralize(["oferta", "oferty", "ofert"])(count)}${ending}`;
                  })
            : [];

    const promotionsCount = stats.promotions_count_total || 0;

    const roomPhraseNominative = `${roomCount} ${pluralize(["pokój", "pokoje", "pokoi"])(roomCount)}`;
    const roomAdjBeforeNoun = `${roomCount}-pokoj${pluralize(["owy", "owe", "owe"])(roomCount)}`;
    const roomAdjGenitivePlural = `${roomCount}-pokoj${pluralize(["owego", "owych", "owych"])(roomCount)}`;
    const roomAdjGenitiveSingular = `${roomCount}-pokoj${pluralize(["owego", "owego", "owego"])(roomCount)}`;

    const introHeadline = `FAQ Mieszkania ${roomAdjBeforeNoun} ${regionName} – dane ${BRAND_NAME}`;

    const countAnswerIntro = `Aktualnie na ${BRAND_NAME} ${where} ${pluralize([
        `dostępne jest ${numberFormat(propertiesForRooms)} nowe mieszkanie ${roomAdjBeforeNoun}`,
        `dostępne są ${numberFormat(propertiesForRooms)} nowe mieszkania ${roomAdjBeforeNoun}`,
        `dostępnych jest ${numberFormat(propertiesForRooms)} nowych mieszkań ${roomAdjGenitivePlural}`
    ])(
        propertiesForRooms
    )}.${offersFlatsCount > 1 ? ` Są one rozmieszczone w ${pluralize([`${numberFormat(offersFlatsCount)} inwestycji deweloperskiej`, `${numberFormat(offersFlatsCount)} inwestycjach deweloperskich`, `${numberFormat(offersFlatsCount)} inwestycjach deweloperskich`])(offersFlatsCount)}.` : ""} ${upperFirst(roomPhraseNominative)} stanowią ${numberFormat(sharePercent)} proc. całej oferty nowych mieszkań ${where}.`;

    const sections: ILocationGenericSection[] = [
        {
            headline: introHeadline,
            paragraphs: []
        },
        {
            headline: `Ile mieszkań ${roomAdjGenitivePlural} jest dostępnych w sprzedaży ${where}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: countAnswerIntro
                },
                ...(roomCount === 4
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `Oferta ${roomCount} pokoi jest bardziej ograniczona niż mniejszych mieszkań – to naturalny efekt rynku. Warto śledzić nowe etapy inwestycji lub uruchomić powiadomienie mailowe.`
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : [])
            ]
        },
        {
            headline:
                roomCount === 2
                    ? `Ile kosztuje mieszkanie ${roomAdjBeforeNoun} ${where} w ${year} roku?`
                    : `Ile kosztuje nowe mieszkanie ${roomAdjBeforeNoun} ${where} w ${year} roku?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: (() => {
                        const parts = [
                            priceM2 > 0
                                ? `Średnia cena ${roomCount === 2 ? "metra kwadratowego mieszkania" : "za metr kwadratowy mieszkania"} ${roomAdjGenitiveSingular} wynosi obecnie ${priceFormat(priceM2)}. `
                                : "",
                            roomCount === 4
                                ? priceMin > 0 &&
                                  `Najtańsze dostępne mieszkanie ${roomAdjBeforeNoun} ${where} kosztuje ${priceFormat(priceMin)} (${monthYearDisplay}).`
                                : priceMin > 0 &&
                                  `Najtańsze nowe mieszkanie ${roomCount} pokojowe ${where} kosztuje w ${monthLocative} ${year} - ${priceFormat(priceMin)}.`
                        ].filter(Boolean) as string[];
                        const joined = parts.join("");
                        return joined || `Szczegółowe ceny mieszkań ${roomAdjGenitivePlural} ${where} zależą od inwestycji — sprawdź listę ofert i filtr ceny.`;
                    })()
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Ceny różnią się istotnie w zależności od lokalizacji, piętra i standardu. W menu rozwijanym "Sortuj" na liście ofert wybierz opcję "Najtańsze", aby uporządkować oferty ${roomCount} pokoi według najniższej ceny.`
                }
            ]
        },
        {
            headline: `Jakie metraże mają mieszkania ${roomAdjBeforeNoun} ${where}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text:
                        areaMin === areaMax
                            ? `Mieszkania ${roomAdjBeforeNoun} mają powierzchnię ${areaMin} mkw.`
                            : `Mieszkania ${roomAdjBeforeNoun} mają powierzchnię od ${areaMin} do ${areaMax} mkw.`
                },
                ...(roomCount === 2
                    ? ([
                          ...(twoRoomAreaAvailabilityListItems.length > 0
                              ? ([
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Dostępność wg metrażu:"
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: twoRoomAreaAvailabilityListItems
                                    }
                                ] as ILocationGenericSection["paragraphs"])
                              : [])
                      ] as ILocationGenericSection["paragraphs"])
                    : roomCount === 3
                      ? ([
                            {
                                type: ParagraphType.TEXT,
                                text: "Standardowy układ to salon z aneksem kuchennym oraz dwie oddzielne sypialnie, co zapewnia prywatność każdemu domownikowi."
                            }
                        ] as ILocationGenericSection["paragraphs"])
                      : ([
                            {
                                type: ParagraphType.TEXT,
                                text: "Standardowy układ obejmuje salon z aneksem kuchennym, 3 oddzielne sypialnie (lub 2 sypialnie + gabinet) oraz najczęściej dwie łazienki lub łazienkę z osobnym WC."
                            }
                        ] as ILocationGenericSection["paragraphs"]))
            ]
        },
        ...(roomCount === 3 || roomCount === 4
            ? ([
                  {
                      headline: `Dla kogo jest mieszkanie ${roomAdjBeforeNoun}?`,
                      paragraphs:
                          roomCount === 3
                              ? [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Mieszkanie ${roomAdjBeforeNoun} ${where} to doskonały wybór dla:`
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: [
                                            "rodzin z jednym dzieckiem – każdy domownik ma własną sypialnię,",
                                            "par pracujących zdalnie – jedna sypialnia jako gabinet,",
                                            "osób szukających lokum z przestrzenią do aranżacji."
                                        ]
                                    },
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Sprawdź filtry na liście – możesz wybrać m.in. mieszkania z ogródkiem na parterze."
                                    }
                                ]
                              : [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Mieszkanie ${roomAdjBeforeNoun} ${where} to idealne rozwiązanie dla:`
                                    },
                                    {
                                        type: ParagraphType.LIST,
                                        text: [
                                            "rodzin z dwójką lub trójką dzieci – każde dziecko ma własny pokój,",
                                            "osób pracujących zdalnie, dla których ważny jest komfortowy gabinet,",
                                            "kupujących, dla których priorytetem jest większa przestrzeń."
                                        ]
                                    },
                                    {
                                        type: ParagraphType.TEXT,
                                        text: "Sprawdź opcje z garażem podziemnym i komórką lokatorską."
                                    }
                                ]
                  }
              ] as ILocationGenericSection[])
            : []),
        {
            headline: `Kiedy będzie można odebrać klucze do nowego mieszkania ${roomAdjGenitiveSingular}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Dostępność mieszkań ${roomAdjGenitivePlural} w ${monthLocative} ${year} ${where} wygląda następująco:`
                },
                ...(readyTimelineItems.length > 0
                    ? ([
                          {
                              type: ParagraphType.LIST,
                              text: readyTimelineItems
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),
                {
                    type: ParagraphType.TEXT,
                    text: `Filtruj oferty korzystając z opcji „termin oddania”.`
                }
            ]
        },
        ...(roomCount === 2
            ? ([
                  {
                      headline: `Czy deweloperzy oferują promocje na mieszkania ${roomAdjBeforeNoun} ${where}?`,
                      paragraphs:
                          promotionsCount > 0
                              ? [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Aktualnie deweloperzy przygotowali ${numberFormat(promotionsCount)} ${pluralize([
                                            "ofertę promocyjną",
                                            "oferty promocyjne",
                                            "ofert promocyjnych"
                                        ])(
                                            promotionsCount
                                        )} na zakup mieszkania ${roomAdjGenitiveSingular} ${where}. Warto zapytać dewelopera o szczegóły – promocje często obejmują miejsce postojowe lub obniżoną cenę przy szybkiej decyzji.`
                                    }
                                ]
                              : [
                                    {
                                        type: ParagraphType.TEXT,
                                        text: `Aktualnie w inwestycjach ${where} nie obowiązują promocje na mieszkania ${roomAdjBeforeNoun}. Zapisz się na newsletter, żeby otrzymać powiadomienie, gdy się to zmieni.`
                                    }
                                ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];

    return sections;
};
